import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Building2, Users2, FilePlus2, History,
  ShieldCheck, ScrollText, LogOut, FileText, Database,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadFile } from "@/lib/api";
import { toast } from "sonner";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/companies", label: "Companies", icon: Building2 },
  { to: "/shareholders", label: "Shareholders", icon: Users2 },
  { to: "/create-dividend", label: "Create Dividend", icon: FilePlus2 },
  { to: "/history", label: "Dividend History", icon: History },
];

const adminItems = [
  { to: "/users", label: "Users", icon: ShieldCheck },
  { to: "/audit", label: "Audit Trail", icon: ScrollText },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleBackup = async () => {
    try {
      await downloadFile("/backup", "backup.json");
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error("Backup failed");
    }
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive
        ? "bg-cass-blue text-white"
        : "text-white/70 hover:bg-white/10 hover:text-white"
    }`;

  return (
    <div className="flex min-h-screen bg-cass-surface">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-cass-navy px-4 py-6">
        <div className="mb-8 flex items-center gap-2 px-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-cass-blue">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-head text-base font-bold text-white leading-tight">CASS Dividends</div>
            <div className="text-[11px] text-white/50">Voucher Tool</div>
          </div>
        </div>

        <nav className="flex-1 space-y-1">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={linkClass} data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}>
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
          {isAdmin && (
            <div className="pt-4">
              <div className="px-3 pb-2 text-[11px] font-bold uppercase tracking-wider text-white/40">Administration</div>
              {adminItems.map((item) => (
                <NavLink key={item.to} to={item.to} className={linkClass} data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}>
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        {isAdmin && (
          <Button variant="ghost" onClick={handleBackup} data-testid="backup-button"
            className="mb-2 justify-start text-white/70 hover:bg-white/10 hover:text-white">
            <Database className="mr-2 h-4 w-4" /> Backup Database
          </Button>
        )}

        <div className="border-t border-white/10 pt-4">
          <div className="mb-2 px-2">
            <div className="text-sm font-medium text-white truncate">{user?.name}</div>
            <div className="text-[11px] text-white/50 capitalize">{user?.role}</div>
          </div>
          <Button variant="ghost" onClick={handleLogout} data-testid="logout-button"
            className="w-full justify-start text-white/70 hover:bg-white/10 hover:text-white">
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="ml-64 flex-1 min-h-screen">
        <div className="mx-auto max-w-7xl px-8 py-8">{children}</div>
      </main>
    </div>
  );
}

export function PageHeader({ title, description, children }) {
  return (
    <div className="mb-8 flex items-start justify-between gap-4">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight text-cass-navy">{title}</h1>
        {description && <p className="mt-1 text-sm text-cass-navy/60">{description}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}
